import * as React from "react";
import { css } from "@nju/css";
import { Icon } from "@nju/icons";
import { Box } from "../Box";

interface IPaginationProps {
  pages: number;
  offset: number;
  currentPage: number;
  onNext: () => void;
  onPrev: () => void;
  onSelect: (page: number) => void;
}

export function Pagination({
  pages,
  offset,
  currentPage,
  onNext,
  onPrev,
  onSelect,
}: IPaginationProps) {
  const items = React.useMemo(() => {
    const elements = [1, pages];

    for (
      let index = Math.max(currentPage - offset, 1);
      index <= Math.min(currentPage + offset, pages);
      index++
    ) {
      elements.push(index);
    }
    return [
      ...new Set(
        elements.sort((a, b) => {
          return a - b;
        }),
      ),
    ];
  }, [pages, offset, currentPage]);

  if (pages < 2) {
    // eslint-disable-next-line unicorn/no-null
    return null;
  }

  return (
    <Box
      align="center"
      className={styles.paginationWrapper()}
      display="flex"
      justify="center"
    >
      <button
        className={currentPage !== 1 ? styles.arrowButtons() : styles.hidden()}
        onClick={onPrev}
      >
        <Icon height={24} name="arrow-left" tone="accent" width={24} />
      </button>
      {items.map((item, index) => {
        const next = items[index + 1];

        const shouldShowDots = Number.isInteger(next) && next !== item + 1;
        const isCurrent = currentPage === item;
        return (
          <React.Fragment key={`${item}-button`}>
            <button
              className={isCurrent ? styles.active() : styles.pageButtons()}
              disabled={isCurrent}
              onClick={() => {
                return onSelect(item - 1);
              }}
            >
              {item}
            </button>
            {shouldShowDots && <span>...</span>}
          </React.Fragment>
        );
      })}
      <button
        className={
          currentPage !== pages ? styles.arrowButtons() : styles.hidden()
        }
        onClick={onNext}
      >
        <Icon height={24} name="arrow-right" tone="accent" width={24} />
      </button>
    </Box>
  );
}

const styles = {
  pageButtons: css({
    px: "$15",
    py: "$5",
    border: "none",
    background: "transparent",
    cursor: "pointer",
    lineHeight: "10px",
  }),
  arrowButtons: css({
    border: "none",
    background: "transparent",
    cursor: "pointer",
    lineHeight: "10px",
  }),
  active: css({
    borderRadius: "$30",
    border: "none",
    backgroundColor: "$cerulean",
    color: "$white",
    fontWeight: "bold",
    px: "$15",
    py: "$5",
  }),
  paginationWrapper: css({
    gap: "20px",
  }),
  hidden: css({
    visibility: "hidden",
  }),
};
